import React, { useRef, useState } from 'react';
import DishList from './DishList';
import LoadingComponent from './LoadingComponent';
import PersonalizationForm from './PersonalizationForm';
import api from '../api';

function IngredientInput({ onBack }) {
  const [ingredient, setIngredient]                     = useState('');
  const [suggestions, setSuggestions]                   = useState([]);
  const [ingredientsList, setIngredientsList]           = useState([]);
  const [dishList, setDishList]                         = useState(null);
  const [loading, setLoading]                           = useState(false);
  const [error, setError]                               = useState(null);
  const [showPersonalizationForm, setShowPersonalizationForm] = useState(false);
  const isSubmitting = useRef(false);

  const fetchSuggestions = async (query) => {
    if (!query) { setSuggestions([]); return; }
    try {
      const response = await api.get('/recipe/ingre/suggestions', { params: { typedName: query } });
      setSuggestions(response.data);
    } catch (err) {
      console.error('Error fetching suggestions:', err.response?.data || err.message);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setIngredient(value);
    fetchSuggestions(value);
  };

  const addIngredient = (name) => {
    if (name && !ingredientsList.includes(name)) {
      setIngredientsList([...ingredientsList, name]);
      setIngredient('');
      setSuggestions([]);
    }
  };

  const removeIngredient = (name) => {
    setIngredientsList(ingredientsList.filter((i) => i !== name));
  };

  const handleSubmit = async () => {
    if (isSubmitting.current || ingredientsList.length === 0) return;

    isSubmitting.current = true;
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/recipe/dishes', { ingredients: ingredientsList });
      const firstItem = response.data[0];
      if (typeof firstItem === 'string' && firstItem.startsWith('Error:')) {
        setError(firstItem.replace('Error: ', ''));
      } else {
        setDishList(response.data);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'An unexpected error occurred';
      setError(msg);
    } finally {
      isSubmitting.current = false;
      setLoading(false);
    }
  };

  if (showPersonalizationForm) {
    return (
      <PersonalizationForm
        ingredients={ingredientsList}
        onBack={() => setShowPersonalizationForm(false)}
      />
    );
  }

  if (dishList) {
    return <DishList dishes={dishList} onBack={() => setDishList(null)} />;
  }

  if (loading) return <LoadingComponent />;

  return (
    <div className="content-panel discovery-page">
      <button className="back-btn" onClick={onBack}>
        &#8592; Back
      </button>

      <section className="feature-page-banner ingredient-page-banner">
        <img
          src="https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=1000&q=80"
          alt="Fresh vegetables and herbs ready for cooking"
          width="1000"
          height="560"
        />
        <div className="feature-page-banner-copy">
          <span className="eyebrow">A little magic from your pantry</span>
          <h1>What's in your kitchen?</h1>
          <p>Pick a few ingredients. We’ll find something lovely to make with them.</p>
        </div>
      </section>

      <div className="section-header">
        <h2>Choose your ingredients</h2>
        <p>Add ingredients you have and we'll find the best recipes for you.</p>
      </div>

      {/* Ingredient Search — overflow:visible so the dropdown escapes the card */}
      <div className="card-custom" style={{ padding: '28px', marginBottom: '20px', overflow: 'visible' }}>
        <label className="section-label">Add an Ingredient</label>
        <div style={{ position: 'relative' }}>
          <input
            id="ingredient-input"
            type="text"
            className="input-custom"
            placeholder="e.g. Tomato, Onion, Chicken…"
            value={ingredient}
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addIngredient(ingredient))}
            autoComplete="off"
            aria-autocomplete="list"
            aria-controls="ingredient-suggestions"
            style={{ paddingLeft: '44px' }}
          />
          <span style={{
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '1.1rem',
            pointerEvents: 'none',
          }}>🥕</span>

          {suggestions.length > 0 && (
            <ul id="ingredient-suggestions" className="suggestions-dropdown" role="listbox" aria-label="Ingredient suggestions" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {suggestions.map((s) => (
                <li key={s} role="presentation">
                  <button type="button" role="option" aria-selected="false" onClick={() => addIngredient(s)}>
                    🌿 {s}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {ingredient && !suggestions.includes(ingredient) && (
          <button
            className="btn-ghost-custom"
            style={{ marginTop: '10px' }}
            onClick={() => addIngredient(ingredient)}
          >
            + Add "{ingredient}" manually
          </button>
        )}
      </div>

      {/* Selected Ingredients */}
      <div className="card-custom" style={{ padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span className="section-label" style={{ margin: 0 }}>
            Selected Ingredients
          </span>
          {ingredientsList.length > 0 && (
            <span className="badge-custom badge-orange">
              {ingredientsList.length} added
            </span>
          )}
        </div>

        {ingredientsList.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '32px 16px',
            color: 'var(--color-text-light)',
            fontSize: '0.9rem',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🧺</div>
            No ingredients added yet. Start typing above!
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {ingredientsList.map((ing, i) => (
              <span key={i} className="tag-chip">
                {ing}
                <button
                  className="remove-btn"
                  onClick={() => removeIngredient(ing)}
                  title={`Remove ${ing}`}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '14px 18px',
          background: 'var(--color-danger-light)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 'var(--radius-md)',
          color: '#991B1B',
          fontSize: '0.9rem',
          marginBottom: '20px',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button
          className="btn-green-custom"
          onClick={handleSubmit}
          disabled={ingredientsList.length === 0}
          style={ingredientsList.length === 0 ? { opacity: 0.5, cursor: 'not-allowed', transform: 'none', boxShadow: 'none' } : {}}
        >
          🍳 Generate Recipes
        </button>
        <button
          className="btn-primary-custom"
          onClick={() => setShowPersonalizationForm(true)}
          disabled={ingredientsList.length === 0}
          style={ingredientsList.length === 0 ? { opacity: 0.5, cursor: 'not-allowed', transform: 'none', boxShadow: 'none' } : {}}
        >
          ✨ Personalise
        </button>
        <button className="btn-secondary-custom" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}

export default IngredientInput;
